import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// Async thunks
export const fetchCampaigns = createAsyncThunk(
  'campaigns/fetchCampaigns',
  async () => {
    const response = await axios.get(`/merchants/email-campaigns`);
    return response.data.campaigns || response.data || [];
  }
);

export const fetchCampaignById = createAsyncThunk(
  'campaigns/fetchCampaignById',
  async ({ campaignId }) => {
    const response = await axios.get(`/merchants/email-campaigns/${campaignId}`);
    return response.data.campaign || response.data;
  }
);

export const createCampaign = createAsyncThunk(
  'campaigns/createCampaign',
  async ({ campaignData }) => {
    const response = await axios.post(`/merchants/email-campaigns`, campaignData);
    return response.data.campaign || response.data;
  }
);

export const updateCampaign = createAsyncThunk(
  'campaigns/updateCampaign',
  async ({ campaignId, campaignData }) => {
    const response = await axios.put(`/merchants/email-campaigns/${campaignId}`, campaignData);
    return response.data.campaign || response.data;
  }
);

export const deleteCampaign = createAsyncThunk(
  'campaigns/deleteCampaign',
  async ({ campaignId }) => {
    await axios.delete(`/merchants/email-campaigns/${campaignId}`);
    return campaignId;
  }
);

export const sendCampaign = createAsyncThunk(
  'campaigns/sendCampaign',
  async ({ campaignId }) => {
    const response = await axios.post(`/merchants/email-campaigns/${campaignId}/send`);
    return response.data.campaign || response.data;
  }
);

export const testCampaign = createAsyncThunk(
  'campaigns/testCampaign',
  async ({ campaignId, testEmail }) => {
    const response = await axios.post(`/merchants/email-campaigns/${campaignId}/test`, { testEmail });
    return response.data;
  }
);

const initialState = {
  campaigns: [],
  currentCampaign: null,
  loading: false,
  error: null
};

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCampaign: (state) => {
      state.currentCampaign = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch campaigns
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch campaign by ID
      .addCase(fetchCampaignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload;
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Create campaign
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = [action.payload, ...state.campaigns];
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Update campaign
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false;
        const campaignId = action.payload.id || action.payload._id;
        const index = state.campaigns.findIndex(
          campaign => (campaign.id || campaign._id) === campaignId
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload;
        }
        const currentId = state.currentCampaign?.id || state.currentCampaign?._id;
        if (currentId && currentId === campaignId) {
          state.currentCampaign = action.payload;
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete campaign
      .addCase(deleteCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(campaign =>
          (campaign.id || campaign._id) !== action.payload
        );
        const currentId = state.currentCampaign?.id || state.currentCampaign?._id;
        if (currentId && currentId === action.payload) {
          state.currentCampaign = null;
        }
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const { clearError, clearCurrentCampaign } = campaignsSlice.actions;
export default campaignsSlice.reducer;
